import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { isAxiosError } from "axios";
import { jwtDecode } from "jwt-decode";

import facialSense_logo from "../../assets/images/logo/CheckIDBioSense_light.svg";
import notfound_logo from "../../assets/images/logo/notfound.svg";
import US from "../../assets/images/flag/US.png";
import VN from "../../assets/images/flag/VN.png";
import { ControlpanelPrimaryButton } from "../../components/StyledButton";
import { IoIosArrowRoundBack } from "react-icons/io";
import { SpinnerRing } from "../../components/CustomSpinner";
import { activeAccount } from "../../axios/Account/AccountApi";
import { getConfigsLogin } from "../../axios/Login/loginApi";
import { LOGIN_TYPE } from "../../assets/data/common";
import { setAccessToken } from "../../utils/Function/commonFunction";
import { useLoading } from "../../context/LoadingContext";

const STATUS = {
  ACTIVATING: "activating",
  SUCCESS: "success",
  ERROR: "error",
};

const setCookie = (name, value, options = {}) => {
  let updatedCookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  if (options.expires instanceof Date) {
    updatedCookie += `; expires=${options.expires.toUTCString()}`;
  }
  if (options.path) {
    updatedCookie += `; path=${options.path}`;
  }
  document.cookie = updatedCookie;
};

/** Lưu session giống luồng SSO login để AuthContext bootstrap vào app */
const persistLoginSession = async (tokenData) => {
  const { data: configs } = await getConfigsLogin();
  const storageKey = configs?.key_session_storage;
  if (!storageKey) {
    throw new Error("Missing key_session_storage");
  }

  const accessToken = tokenData.access_token;
  const refreshToken = tokenData.refresh_token;
  const idToken = tokenData.id_token;
  const expiresIn = Number(tokenData.expires_in) || 3600;

  if (!accessToken || !refreshToken) {
    throw new Error("Missing access_token or refresh_token");
  }

  let profile = {};
  try {
    if (idToken) {
      profile = jwtDecode(idToken);
    } else {
      profile = jwtDecode(accessToken);
    }
  } catch {
    profile = {};
  }

  const expiresAtSec = Math.floor(Date.now() / 1000) + expiresIn;
  const sessionUser = {
    id_token: idToken,
    access_token: accessToken,
    refresh_token: refreshToken,
    token_type: tokenData.token_type || "Bearer",
    scope: "openid profile identity email",
    profile,
    expires_at: expiresAtSec,
  };

  localStorage.setItem(storageKey, JSON.stringify(sessionUser));
  setAccessToken(accessToken, LOGIN_TYPE.normal);

  let cookieExpires = new Date(Date.now() + expiresIn * 1000);
  try {
    const { exp } = jwtDecode(refreshToken);
    if (exp) {
      cookieExpires = new Date(exp * 1000);
    }
  } catch {
    // giữ expires theo access token
  }

  setCookie("accessToken", accessToken, { expires: cookieExpires, path: "/" });
  setCookie("refreshToken", refreshToken, { expires: cookieExpires, path: "/" });
};

const ActiveAccount = () => {
  const { t, i18n } = useTranslation();
  const [show, hide] = useLoading();
  const [openLang, setOpenLang] = useState(false);
  const [status, setStatus] = useState(STATUS.ACTIVATING);
  const calledRef = useRef(false);

  const languages = [
    { code: "vi", label: "Tiếng Việt" },
    { code: "en", label: "English" },
  ];

  const currentLang =
    languages.find((l) => l.code === i18n.language)?.label || "Language";

  const changeLang = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem("i18nextLngId", code === "vi" ? "1" : "2");
    setOpenLang(false);
  };

  const goToLogin = () => {
    window.location.href = `${window.location.origin}/portal/login`;
  };

  const goToApp = () => {
    // AuthContext + PageRouters sẽ điều hướng admin → dashboard, employee → event-log
    window.location.href = `${window.location.origin}/portal/`;
  };

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get("code");
    const stateFromQuery = searchParams.get("state");
    // Ưu tiên bản encode đã lưu từ invitation; tránh gửi state đã decode (/ , + , =)
    const stateFromStorage = localStorage.getItem("activateCode");
    const state =
      stateFromStorage ||
      (/%[0-9A-Fa-f]{2}/.test(stateFromQuery || "")
        ? stateFromQuery
        : encodeURIComponent(stateFromQuery || ""));

    if (!code || !state) {
      setStatus(STATUS.ERROR);
      toast.error(t("accountActivation.missing-params"));
      return;
    }

    const activate = async () => {
      try {
        show();
        setStatus(STATUS.ACTIVATING);

        const { data: responseData } = await activeAccount({
          code,
          state,
          langId: localStorage.getItem("i18nextLngId") || "1",
        });

        const payload =
          typeof responseData === "string"
            ? JSON.parse(responseData)
            : responseData;
        const tokenData = payload?.data?.[0];

        if (!tokenData?.access_token || !tokenData?.refresh_token) {
          throw new Error("Invalid activation token response");
        }

        await persistLoginSession(tokenData);
        localStorage.removeItem("activateCode");
        setStatus(STATUS.SUCCESS);
        toast.success(t("sign-up-success.your-account-is-now-active"));
        // API xong → vào app ngay (không setTimeout)
        goToApp();
      } catch (error) {
        setStatus(STATUS.ERROR);
        let toastMsg = t("ERRORMSG_0001");
        if (isAxiosError(error)) {
          const data = error.response?.data;
          if (typeof data === "string") {
            toastMsg = t(data) !== data ? t(data) : data;
          } else if (data?.message) {
            toastMsg = data.message;
          } else if (data?.error) {
            toastMsg = data.error;
          }
        } else if (error?.message) {
          toastMsg = error.message;
        }
        toast.error(toastMsg);
      } finally {
        hide();
      }
    };

    activate();
  }, [t, show, hide]);

  const titleKey =
    status === STATUS.SUCCESS
      ? "accountActivation.success-title"
      : status === STATUS.ERROR
        ? "accountActivation.error-title"
        : "accountActivation.activating-title";

  const messageKey =
    status === STATUS.SUCCESS
      ? "common.your-account-is-now-active"
      : status === STATUS.ERROR
        ? "accountActivation.error-message"
        : "accountActivation.activating-message";

  return (
    <div className="min-h-screen flex flex-col px-4">
      <div className="w-full flex items-center justify-between py-4">
        <img src={facialSense_logo} alt="logo" className="h-14 w-auto" />

        <div className="relative">
          <button
            onClick={() => setOpenLang((prev) => !prev)}
            className="
              flex items-center gap-2
              px-3 py-2
              border border-[#E5E7EB]
              rounded-xl
              bg-white
              hover:bg-gray-50
              transition
            "
          >
            <img
              src={i18n.language === "vi" ? VN : US}
              alt="flag"
              className="w-6 h-6 rounded-full object-cover"
            />
            <span className="text-sm text-gray-700">{currentLang}</span>
            <svg
              className={`w-4 h-4 transition-transform ${
                openLang ? "rotate-180" : ""
              }`}
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {openLang && (
            <div
              className="
                absolute right-0 mt-2 w-40
                bg-white
                border border-[#E5E7EB]
                rounded-lg
                shadow-lg
                z-50
                overflow-hidden
              "
            >
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLang(lang.code)}
                  className={`
                    w-full text-left px-3 py-2 text-sm
                    hover:bg-gray-100
                    ${
                      i18n.language === lang.code
                        ? "bg-gray-50 font-semibold"
                        : ""
                    }
                  `}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center">
        <div
          className="
            w-full max-w-6xl
            flex flex-col-reverse lg:flex-row
            items-center justify-center
            gap-10 lg:gap-20
          "
        >
          <div className="flex flex-col items-center lg:items-start text-center lg:text-left max-w-xl space-y-6">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
              {t(titleKey)}
            </h1>

            <p className="text-[#6B7280] text-base md:text-lg">{t(messageKey)}</p>

            {status === STATUS.ACTIVATING && (
              <div className="flex justify-center lg:justify-start w-full">
                <SpinnerRing loading size={40} color="#3B82F6" />
              </div>
            )}

            {status === STATUS.SUCCESS && (
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <ControlpanelPrimaryButton
                  onClick={goToApp}
                  sx={{ textTransform: "none" }}
                >
                  <p>{t("button.go-to-control-panel")}</p>
                </ControlpanelPrimaryButton>
              </div>
            )}

            {status === STATUS.ERROR && (
              <div>
                <button
                  onClick={goToLogin}
                  className="
                    flex items-center
                    text-blue-600
                    hover:underline
                    transition
                  "
                >
                  <IoIosArrowRoundBack size={22} className="mr-1" />
                  {t("common.back-to-home")}
                </button>
              </div>
            )}
          </div>

          <div className="flex justify-center">
            <img
              src={notfound_logo}
              alt="Active account"
              className="
                w-[260px]
                sm:w-[320px]
                md:w-[420px]
                lg:w-[500px]
                h-auto
                object-contain
              "
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActiveAccount;
